import { useApolloClient } from '@apollo/client';
import {
  Enum_Staff_Jobfunction,
  Enum_Staff_Role,
  MeQuery,
  notNull,
  OrgConfigurationValue,
  useLogoutMutation,
  useMeLazyQuery,
} from '@cyren/common-lib';
import produce from 'immer';
import { first } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import useAppConfig from '../app/use-app-config';
import { AppStateCookie } from '../app/use-app-state';
import { convStaff } from '../gql-tools/utils/convert';
import { setAccessToken } from '../graphql/client';
import { useAdminParams } from '../survey/use-admin-params';
import { onError } from '../utils/apollo-utils';
import { AuthState } from './auth-state';
import { tokenManager } from '../v2/shared/utils/token-manager';
import { queryClient } from '../v2/api/query-client';
import AuthContainer from '../v2/shared/containers/AuthContainer';

export default function useAuth({
  navigateToAuthed,
  navigateToUnAuthed,
}: {
  navigateToAuthed?: string;
  navigateToUnAuthed?: string;
} = {}) {
  const loc = useLocation();
  const [sParams] = useSearchParams();
  const { orgIdParam } = useAdminParams();

  const client = useApolloClient();
  const nav = useNavigate();

  const [appConfig, setAppConfig] = useAppConfig();
  const { authLoading, authInit } = appConfig || {};
  const [authState, setAuthState] = useRecoilState(AuthState);
  const { profiles, selectedProfile, user, jobFunctionOverride } = authState || {};
  const setAppStateCookie = useSetRecoilState(AppStateCookie);

  // AuthContainer for reactive login state
  const authContainer = AuthContainer.useContainer();

  const [logoutMutation] = useLogoutMutation({
    onError,
    onCompleted: () => {
      client.clearStore();
    },
  });

  // QA/QC FIX: [CRITICAL] - useEffect dependency array incomplete causing stale closures
  // LOGIC ISSUE: Missing 'nav' and 'sParams' in dependency array can cause navigation failures with stale references
  // SOLUTION: Include all referenced variables in dependency array or memoize with useCallback
  // IMPACT: Authentication redirects may fail or use stale navigation parameters

  /**
   * Navigate to user to somewhere when authed
   */
  useEffect(() => {
    if (navigateToAuthed && authInit && authState?.jwt) {
      nav(`${navigateToAuthed}${sParams ? `?${sParams}` : ''}`);
    }

    if (navigateToUnAuthed && authInit && !authState?.jwt) {
      nav(`${navigateToUnAuthed}${sParams ? `?${sParams}` : ''}`);
    }
  }, [authInit, authState?.jwt, navigateToAuthed, navigateToUnAuthed, nav, sParams]);

  const handleLogin = async (resp: MeQuery['meV2']) => {
    const { jwt, user: nUser, staffs: rStaff, orgConfigurations } = resp || {};

    // V2 토큰 관리 통합: GraphQL + Axios 모두에 토큰 설정
    const token = jwt || authState?.jwt || null;
    tokenManager.setToken(token);

    // Set login state for reactive API calls
    if (token) {
      authContainer.setIsLogin(true);
    }

    const nStaffs = rStaff?.data?.map(convStaff).filter(notNull);
    const urlSelectedProfile = nStaffs?.find((st) => st.organization?.id === orgIdParam);
    const nProfile = urlSelectedProfile || first(nStaffs);

    setAuthState({
      jwt: token,
      user: nUser,
      profiles: nStaffs,
      selectedProfile: nProfile,
      orgConfigurationValues: (orgConfigurations as OrgConfigurationValue[]) || []
    });

    if (['/admin', '/admin/'].includes(loc.pathname)) {
      nav(`/admin/${nProfile?.organization?.id}`);
    }
  };

  const [fetchMe, { loading }] = useMeLazyQuery({
    fetchPolicy: 'cache-first',
    // this line triggers refetching every time gql query contains UserPermissionsUser fragment.
    // fetchPolicy: 'cache-and-network',
    onCompleted: async (data) => {
      setAppConfig({ authInit: true, authLoading: false });

      if (data?.meV2) {
        await handleLogin(data.meV2);
      } else {
        if (window.location.pathname.includes('/sp/')) {
          console.log('meV2 data is null, but /sp/ 경로이므로 admin 리다이렉트 생략');
          return;
        }
        console.log('meV2 data is null, redirecting to login');
        setAuthState(null);
        nav('/admin/');
      }
    },
    onError: (error) => {
      console.error('meV2 query error:', error);
      // 토큰 갱신 실패 시 로그인 페이지로 리다이렉트
      setAppConfig({ authInit: true, authLoading: false });
      setAuthState(null);
      
      // 로그인 페이지로 리다이렉트
      nav('/admin/');
    },
  });

  // QA/QC FIX: [CRITICAL] - useEffect dependency array missing critical dependencies
  // LOGIC ISSUE: Missing dependencies can cause stale closures and infinite loops. The effect depends on values that aren't in the dependency array.
  // SOLUTION: Add all referenced variables and functions to dependency array, or use useCallback for functions
  // IMPACT: Can cause authentication state inconsistencies, failed token refreshes, and memory leaks

  // QA/QC FIX: [CRITICAL] - Potential memory leak from unhandled promise rejections
  // LOGIC ISSUE: The catch block doesn't clean up pending requests if component unmounts during fetch
  // SOLUTION: Use AbortController for cancellation and cleanup in useEffect return function
  // IMPACT: Memory leaks and potential crashes from unhandled promise rejections after unmount

  // auth initialize
  useEffect(() => {
    if (!loading && !authLoading && !authInit) {
      setAppConfig({ authLoading: true, authInit: false });

      fetchMe({
        fetchPolicy: 'cache-and-network',
      }).catch((error) => {
        console.error('fetchMe error:', error);
        setAppConfig({ authInit: true, authLoading: false });
        setAuthState(null);
        nav('/admin/');
      });
    }
  }, [authLoading, authInit, loading, fetchMe, setAppConfig, setAuthState, nav]);

  // Handle case where authInit is true but authState is null (token refresh failed)
  useEffect(() => {
    if (authInit && !authLoading && !authState?.jwt) {
      // 만약 URL에 /sp/가 포함되어 있으면 admin 라우팅을 하지 않음
      if (window.location.pathname.includes('/sp/')) {
        console.log('Auth initialized but no valid token, but /sp/ 경로이므로 admin 리다이렉트 생략');
      } else {
        console.log('Auth initialized but no valid token, redirecting to login');
        nav('/admin/');
      }
    }
  }, [authInit, authLoading, authState?.jwt, nav]);

  const handleLogout = useCallback(async () => {
    await logoutMutation({
      onCompleted: () => {
        // V2 토큰 관리 통합: 모든 토큰 삭제
        tokenManager.clearToken();

        // Clear login state
        authContainer.setIsLogin(false);

        // Apollo 캐시 삭제
        client.clearStore();

        // React Query 캐시 삭제
        queryClient.clear();

        // QA/QC FIX: [HIGH] - Aggressive storage clearing may affect other applications
        // LOGIC ISSUE: localStorage.clear() and sessionStorage.clear() remove ALL data, not just app-specific data, potentially breaking other apps on same domain
        // SOLUTION: Only clear application-specific keys using a namespace prefix like 'cyren_':
        // Object.keys(localStorage).forEach(key => { if (key.startsWith('cyren_')) localStorage.removeItem(key); });
        // IMPACT: Can break functionality in other applications sharing the same domain
        // 네임스페이스 기반 localStorage 삭제 (개선)
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('cyren_') || key.startsWith('recoil')) {
            localStorage.removeItem(key);
          }
        });
        sessionStorage.clear();

        // QA/QC FIX: [MEDIUM] - Cookie clearing vulnerability and incomplete domain handling
        // LOGIC ISSUE: Cookie clearing attempts may fail for httpOnly cookies and doesn't handle subdomains properly
        // SOLUTION: Use secure cookie clearing with proper domain and path specifications:
        // - Add secure flag for HTTPS: document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;secure;samesite=strict`
        // - Handle subdomains: document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=.${getRootDomain()}`
        // IMPACT: Incomplete logout may leave authentication tokens accessible, creating security vulnerabilities
        // 쿠키 클리어 (도메인별로)
        const cookies = document.cookie.split(';');
        cookies.forEach(cookie => {
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        });

        setAppConfig({ authLoading: false, authInit: false });
        setAuthState(null);

        setAppStateCookie((st) =>
          produce(st, (dr) => {
            dr.msAuth = false;
          })
        );
      },
    });
  }, [client, setAccessToken, setAppConfig, setAuthState, logoutMutation]);

  const setSelectedProfile = (
    profileId: string,
    { basePath = '/admin' }: { basePath?: string } = {}
  ) => {
    const nSelectedProfile = profiles?.find((p) => p.id === profileId);
    const orgId = nSelectedProfile?.organization?.id;

    setAuthState({
      ...authState,
      selectedProfile: nSelectedProfile,
    });

    nav(`${basePath}/${orgId}`);
  };

  const setJobFunctionOverride = (
    newJobFunctionOverride: Enum_Staff_Jobfunction
  ) => {
    setAuthState({
      ...authState,
      jobFunctionOverride: newJobFunctionOverride,
    });
  };


  const getOrgConfigurationValue = (key: string) => {
    return authState?.orgConfigurationValues?.filter(config => config.orgId === selectedProfile?.organization?.id)
    .find(config => config.key === key);
  }

  return [
    {
      isLoggedIn: !!authState?.jwt,
      authInit,
      authLoading,
      selectedProfile,
      selectedOrg: selectedProfile?.organization,
      orgId: selectedProfile?.organization?.id,
      isAdmin: selectedProfile?.role === Enum_Staff_Role.Admin,
      isSuperAdmin: user?.superAdmin === true,
      jobFunctionOverride,
      jobFunction: jobFunctionOverride || selectedProfile?.jobFunction,
      userId: user?.id,
      profiles,
      loading: authLoading || !authInit || loading,
      ...authState,
      auth: authState?.user,
      authState,
    },
    {
      setSelectedProfile,
      setJobFunctionOverride,
      handleLogin,
      handleLogout,
      navigate: nav,
      nav,
      setAuthState,
      setAppConfig,
      getOrgConfigurationValue,
    },
  ] as const;
}
